import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { templatesAPI } from '../lib/api';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft, Save, Eye, EyeOff, GripVertical, Trash2, Plus,
  Type, Image as ImageIcon, Square, Minus, AlignLeft, Mail,
  Heading1, LayoutTemplate, ArrowUpDown, X, Check, MousePointerClick,
  Layout, ChevronDown, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

// ─── Paleta de blocos ────────────────────────────────────────────────
const PALETTE = [
  { type: 'header',  label: 'Cabeçalho', Icon: Layout,           desc: 'Logo e título da marca' },
  { type: 'heading', label: 'Título',    Icon: Heading1,          desc: 'H1 / H2 / H3' },
  { type: 'text',    label: 'Texto',     Icon: AlignLeft,         desc: 'Parágrafo de texto' },
  { type: 'image',   label: 'Imagem',    Icon: ImageIcon,         desc: 'Imagem com link opcional' },
  { type: 'button',  label: 'Botão',     Icon: MousePointerClick, desc: 'Call-to-action' },
  { type: 'divider', label: 'Divisor',   Icon: Minus,             desc: 'Linha separadora' },
  { type: 'spacer',  label: 'Espaço',    Icon: ArrowUpDown,       desc: 'Espaço em branco' },
  { type: 'footer',  label: 'Rodapé',    Icon: LayoutTemplate,    desc: 'Texto de rodapé / cancelar' },
];

// ─── Props padrão por tipo ────────────────────────────────────────────
const DEFAULTS = {
  header:  { title: 'Tayane Larini', subtitle: '', bgColor: '#A0462D', textColor: '#ffffff', logo: '' },
  heading: { text: 'Seu título aqui', level: 'h2', color: '#2D0C10', align: 'center' },
  text:    { html: '<p>Olá <strong>{{nome}}</strong>,</p><p>Escreva sua mensagem aqui.</p>', color: '#374151' },
  image:   { src: '', alt: '', link: '', width: '100%', align: 'center' },
  button:  { text: 'Clique aqui', url: '#', bgColor: '#A0462D', textColor: '#ffffff', align: 'center', borderRadius: '6' },
  divider: { color: '#E5E7EB', thickness: '1', marginV: '16' },
  spacer:  { height: '32' },
  footer:  { text: 'Você recebe este email por se cadastrar em nossa lista.\n{{unsubscribe_link}}', bgColor: '#F9FAFB', textColor: '#9CA3AF', fontSize: '13' },
};

let uid = 1;
function makeBlock(type) {
  return { id: `b${uid++}`, type, props: { ...DEFAULTS[type] } };
}

// ─── Geração de HTML para email ──────────────────────────────────────
function blockToHtml(b) {
  const p = b.props;
  switch (b.type) {
    case 'header':
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${p.bgColor}">
  <tr><td align="center" style="padding:32px 24px">
    ${p.logo ? `<img src="${p.logo}" alt="${p.title}" style="height:44px;display:block;margin:0 auto 12px">` : ''}
    <div style="color:${p.textColor};font-size:24px;font-weight:700;font-family:Arial,sans-serif">${p.title}</div>
    ${p.subtitle ? `<div style="color:${p.textColor};font-size:14px;margin-top:6px;opacity:.85;font-family:Arial,sans-serif">${p.subtitle}</div>` : ''}
  </td></tr>
</table>`;
    case 'heading': {
      const sz = { h1:'28px', h2:'22px', h3:'18px' }[p.level] || '22px';
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
  <tr><td align="${p.align}" style="padding:16px 24px 8px">
    <${p.level} style="margin:0;color:${p.color};font-size:${sz};font-family:Arial,sans-serif;font-weight:700;line-height:1.3">${p.text}</${p.level}>
  </td></tr>
</table>`;
    }
    case 'text':
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
  <tr><td style="padding:8px 24px;color:${p.color};font-size:15px;font-family:Arial,sans-serif;line-height:1.7">${p.html}</td></tr>
</table>`;
    case 'image':
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
  <tr><td align="${p.align}" style="padding:16px 24px">
    ${p.src
      ? `${p.link ? `<a href="${p.link}" target="_blank">` : ''}<img src="${p.src}" alt="${p.alt}" style="width:${p.width};max-width:100%;display:block;${p.align==='center'?'margin:0 auto':''}">${p.link ? '</a>' : ''}`
      : `<div style="background:#F3F4F6;height:120px;text-align:center;line-height:120px;color:#9CA3AF;font-size:13px;font-family:Arial,sans-serif;border:2px dashed #E5E7EB;border-radius:6px">Sem imagem</div>`}
  </td></tr>
</table>`;
    case 'button':
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
  <tr><td align="${p.align}" style="padding:16px 24px">
    <a href="${p.url}" target="_blank" style="display:inline-block;background:${p.bgColor};color:${p.textColor};text-decoration:none;font-size:15px;font-weight:600;font-family:Arial,sans-serif;padding:12px 28px;border-radius:${p.borderRadius}px">${p.text}</a>
  </td></tr>
</table>`;
    case 'divider':
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
  <tr><td style="padding:${p.marginV}px 24px"><hr style="border:none;border-top:${p.thickness}px solid ${p.color};margin:0"></td></tr>
</table>`;
    case 'spacer':
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
  <tr><td style="height:${p.height}px;font-size:0;line-height:0">&nbsp;</td></tr>
</table>`;
    case 'footer':
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${p.bgColor}">
  <tr><td align="center" style="padding:24px;color:${p.textColor};font-size:${p.fontSize}px;font-family:Arial,sans-serif;line-height:1.7">
    ${p.text.replace(/\n/g,'<br>').replace('{{unsubscribe_link}}',`<a href="{{unsubscribe}}" style="color:${p.textColor}">Cancelar inscrição</a>`)}
  </td></tr>
</table>`;
    default: return '';
  }
}

function generateHtml(blocks, subject) {
  return `<!DOCTYPE html>
<html lang="pt-BR"><head>
<meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>${subject}</title>
</head>
<body style="margin:0;padding:0;background:#f4f4f4;font-family:Arial,sans-serif">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#f4f4f4">
<tr><td align="center" style="padding:24px 0">
<table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="background:#fff;max-width:600px;width:100%">
<tr><td>
${blocks.map(blockToHtml).join('\n')}
</td></tr>
</table>
</td></tr>
</table>
</body></html>`;
}

// ─── Renderização visual dos blocos no canvas ────────────────────────
function BlockCanvas({ block }) {
  const p = block.props;
  switch (block.type) {
    case 'header':
      return (
        <div style={{ background: p.bgColor, padding: '28px 24px', textAlign: 'center' }}>
          {p.logo && <img src={p.logo} alt={p.title} style={{ height: 40, marginBottom: 10, display: 'block', margin: '0 auto 10px' }} />}
          <div style={{ color: p.textColor, fontSize: 20, fontWeight: 700 }}>{p.title}</div>
          {p.subtitle && <div style={{ color: p.textColor, fontSize: 13, marginTop: 4, opacity: .85 }}>{p.subtitle}</div>}
        </div>
      );
    case 'heading': {
      const sz = { h1:24, h2:20, h3:17 }[p.level] || 20;
      return (
        <div style={{ padding: '14px 24px 6px', textAlign: p.align }}>
          <span style={{ color: p.color, fontSize: sz, fontWeight: 700, lineHeight: 1.3 }}>{p.text}</span>
        </div>
      );
    }
    case 'text':
      return (
        <div style={{ padding: '6px 24px', color: p.color, fontSize: 14, lineHeight: 1.7 }}
          dangerouslySetInnerHTML={{ __html: p.html }} />
      );
    case 'image':
      return (
        <div style={{ padding: '14px 24px', textAlign: p.align }}>
          {p.src
            ? <img src={p.src} alt={p.alt} style={{ maxWidth: '100%', width: p.width, display: 'inline-block' }} />
            : <div style={{ background: '#F3F4F6', height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: 13, border: '2px dashed #E5E7EB', borderRadius: 6 }}>
                <ImageIcon size={20} style={{ marginRight: 6 }} /> Sem imagem — cole URL nas propriedades
              </div>}
        </div>
      );
    case 'button':
      return (
        <div style={{ padding: '14px 24px', textAlign: p.align }}>
          <span style={{ display: 'inline-block', background: p.bgColor, color: p.textColor, padding: '10px 24px', borderRadius: parseInt(p.borderRadius), fontSize: 14, fontWeight: 600 }}>
            {p.text}
          </span>
        </div>
      );
    case 'divider':
      return <div style={{ padding: `${p.marginV}px 24px` }}><hr style={{ border: 'none', borderTop: `${p.thickness}px solid ${p.color}`, margin: 0 }} /></div>;
    case 'spacer':
      return <div style={{ height: parseInt(p.height), background: 'repeating-linear-gradient(45deg,#f9fafb,#f9fafb 4px,#f3f4f6 4px,#f3f4f6 8px)' }} />;
    case 'footer':
      return (
        <div style={{ background: p.bgColor, padding: '20px 24px', textAlign: 'center', color: p.textColor, fontSize: parseInt(p.fontSize), lineHeight: 1.7 }}>
          {p.text.split('\n').map((line, i) => (
            <React.Fragment key={i}>{line === '{{unsubscribe_link}}' ? <span style={{ textDecoration: 'underline' }}>Cancelar inscrição</span> : line}{i < p.text.split('\n').length - 1 && <br />}</React.Fragment>
          ))}
        </div>
      );
    default: return null;
  }
}

// ─── Item sortável no canvas ─────────────────────────────────────────
function SortableBlock({ block, selected, onClick, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

  return (
    <div ref={setNodeRef} style={style}
      className={`group relative border-2 rounded-lg overflow-hidden cursor-pointer transition-all ${selected ? 'border-[#A0462D] shadow-md shadow-[#A0462D]/10' : 'border-transparent hover:border-gray-300'}`}
      onClick={onClick}>
      {/* drag handle */}
      <div {...attributes} {...listeners}
        className="absolute left-1 top-1/2 -translate-y-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing p-1 rounded bg-white/90 shadow-sm"
        onClick={e => e.stopPropagation()}>
        <GripVertical size={14} className="text-gray-400" />
      </div>
      {/* delete */}
      <button
        className="absolute right-1 top-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded bg-white/90 shadow-sm text-gray-400 hover:text-red-500"
        onClick={e => { e.stopPropagation(); onDelete(); }}>
        <Trash2 size={13} />
      </button>
      {/* selected indicator */}
      {selected && <div className="absolute inset-y-0 left-0 w-0.5 bg-[#A0462D]" />}
      <BlockCanvas block={block} />
    </div>
  );
}

// ─── Painel de propriedades ──────────────────────────────────────────
function PropField({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
function PInput({ value, onChange, placeholder, type = 'text' }) {
  return <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:border-[#A0462D] transition-colors" />;
}
function PColor({ value, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <input type="color" value={value} onChange={e => onChange(e.target.value)}
        className="w-8 h-8 rounded cursor-pointer border border-gray-200 p-0.5 bg-white" />
      <PInput value={value} onChange={onChange} />
    </div>
  );
}
function PSelect({ value, onChange, options }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:border-[#A0462D] transition-colors">
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
function PTextarea({ value, onChange, rows = 4 }) {
  return <textarea rows={rows} value={value} onChange={e => onChange(e.target.value)}
    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:border-[#A0462D] resize-y transition-colors font-mono" />;
}

function PropertiesPanel({ block, onChange }) {
  if (!block) return (
    <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-400 p-6">
      <MousePointerClick size={28} className="text-gray-200" />
      <p className="text-sm text-center">Clique em um bloco<br />para editar suas propriedades</p>
    </div>
  );

  const set = (key, val) => onChange({ ...block.props, [key]: val });
  const p = block.props;

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide pb-1 border-b border-gray-100">
        {PALETTE.find(x => x.type === block.type)?.label}
      </div>

      {block.type === 'header' && <>
        <PropField label="Título"><PInput value={p.title} onChange={v => set('title', v)} placeholder="Tayane Larini" /></PropField>
        <PropField label="Subtítulo"><PInput value={p.subtitle} onChange={v => set('subtitle', v)} placeholder="(opcional)" /></PropField>
        <PropField label="URL do Logo"><PInput value={p.logo} onChange={v => set('logo', v)} placeholder="https://..." /></PropField>
        <PropField label="Cor de fundo"><PColor value={p.bgColor} onChange={v => set('bgColor', v)} /></PropField>
        <PropField label="Cor do texto"><PColor value={p.textColor} onChange={v => set('textColor', v)} /></PropField>
      </>}

      {block.type === 'heading' && <>
        <PropField label="Texto"><PInput value={p.text} onChange={v => set('text', v)} /></PropField>
        <PropField label="Nível">
          <PSelect value={p.level} onChange={v => set('level', v)} options={[{value:'h1',label:'H1 — grande'},{value:'h2',label:'H2 — médio'},{value:'h3',label:'H3 — pequeno'}]} />
        </PropField>
        <PropField label="Alinhamento">
          <PSelect value={p.align} onChange={v => set('align', v)} options={[{value:'left',label:'Esquerda'},{value:'center',label:'Centro'},{value:'right',label:'Direita'}]} />
        </PropField>
        <PropField label="Cor do texto"><PColor value={p.color} onChange={v => set('color', v)} /></PropField>
      </>}

      {block.type === 'text' && <>
        <PropField label="Conteúdo (HTML)"><PTextarea value={p.html} onChange={v => set('html', v)} rows={8} /></PropField>
        <p className="text-xs text-gray-400">Use <code className="bg-gray-100 px-1 rounded">{'{{nome}}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{{email}}'}</code> como variáveis.</p>
        <PropField label="Cor do texto"><PColor value={p.color} onChange={v => set('color', v)} /></PropField>
      </>}

      {block.type === 'image' && <>
        <PropField label="URL da imagem"><PInput value={p.src} onChange={v => set('src', v)} placeholder="https://..." /></PropField>
        <PropField label="Texto alternativo (alt)"><PInput value={p.alt} onChange={v => set('alt', v)} /></PropField>
        <PropField label="Link ao clicar"><PInput value={p.link} onChange={v => set('link', v)} placeholder="https://..." /></PropField>
        <PropField label="Largura"><PInput value={p.width} onChange={v => set('width', v)} placeholder="100% ou 300px" /></PropField>
        <PropField label="Alinhamento">
          <PSelect value={p.align} onChange={v => set('align', v)} options={[{value:'left',label:'Esquerda'},{value:'center',label:'Centro'},{value:'right',label:'Direita'}]} />
        </PropField>
      </>}

      {block.type === 'button' && <>
        <PropField label="Texto do botão"><PInput value={p.text} onChange={v => set('text', v)} /></PropField>
        <PropField label="URL de destino"><PInput value={p.url} onChange={v => set('url', v)} /></PropField>
        <PropField label="Alinhamento">
          <PSelect value={p.align} onChange={v => set('align', v)} options={[{value:'left',label:'Esquerda'},{value:'center',label:'Centro'},{value:'right',label:'Direita'}]} />
        </PropField>
        <PropField label="Cor de fundo"><PColor value={p.bgColor} onChange={v => set('bgColor', v)} /></PropField>
        <PropField label="Cor do texto"><PColor value={p.textColor} onChange={v => set('textColor', v)} /></PropField>
        <PropField label="Arredondamento (px)"><PInput value={p.borderRadius} onChange={v => set('borderRadius', v)} type="number" /></PropField>
      </>}

      {block.type === 'divider' && <>
        <PropField label="Cor"><PColor value={p.color} onChange={v => set('color', v)} /></PropField>
        <PropField label="Espessura (px)"><PInput value={p.thickness} onChange={v => set('thickness', v)} type="number" /></PropField>
        <PropField label="Espaçamento vertical (px)"><PInput value={p.marginV} onChange={v => set('marginV', v)} type="number" /></PropField>
      </>}

      {block.type === 'spacer' && <>
        <PropField label="Altura (px)"><PInput value={p.height} onChange={v => set('height', v)} type="number" /></PropField>
      </>}

      {block.type === 'footer' && <>
        <PropField label="Texto"><PTextarea value={p.text} onChange={v => set('text', v)} rows={5} /></PropField>
        <p className="text-xs text-gray-400">Use <code className="bg-gray-100 px-1 rounded">{'{{unsubscribe_link}}'}</code> para o link de cancelamento.</p>
        <PropField label="Cor de fundo"><PColor value={p.bgColor} onChange={v => set('bgColor', v)} /></PropField>
        <PropField label="Cor do texto"><PColor value={p.textColor} onChange={v => set('textColor', v)} /></PropField>
        <PropField label="Tamanho da fonte (px)"><PInput value={p.fontSize} onChange={v => set('fontSize', v)} type="number" /></PropField>
      </>}
    </div>
  );
}

// ─── Editor principal ────────────────────────────────────────────────
const BRAND = '#A0462D';

export default function TemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [blocks, setBlocks] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const iframeRef = useRef(null);

  // Carrega template existente
  useEffect(() => {
    if (isNew) {
      setBlocks([makeBlock('header'), makeBlock('text'), makeBlock('button'), makeBlock('footer')]);
      return;
    }
    templatesAPI.get(id)
      .then(r => {
        const t = r.data;
        setName(t.name || '');
        setSubject(t.subject || '');
        setBlocks((t.blocks || []).map(b => ({ ...b, id: b.id || `b${uid++}` })));
      })
      .catch(() => { toast.error('Template não encontrado'); navigate('/templates'); })
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  // Atualiza iframe de preview
  useEffect(() => {
    if (!preview || !iframeRef.current) return;
    const doc = iframeRef.current.contentDocument;
    if (doc) { doc.open(); doc.write(generateHtml(blocks, subject)); doc.close(); }
  }, [preview, blocks, subject]);

  // DnD
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const handleDragEnd = useCallback(({ active, over }) => {
    if (!over || active.id === over.id) return;
    setBlocks(bs => {
      const oi = bs.findIndex(b => b.id === active.id);
      const ni = bs.findIndex(b => b.id === over.id);
      return arrayMove(bs, oi, ni);
    });
  }, []);

  const addBlock = (type) => {
    const b = makeBlock(type);
    setBlocks(bs => [...bs, b]);
    setSelectedId(b.id);
  };

  const deleteBlock = (bid) => {
    setBlocks(bs => bs.filter(b => b.id !== bid));
    if (selectedId === bid) setSelectedId(null);
  };

  const updateBlockProps = (bid, props) => {
    setBlocks(bs => bs.map(b => b.id === bid ? { ...b, props } : b));
  };

  const save = async () => {
    if (!name.trim()) { toast.error('Nome do template é obrigatório'); return; }
    setSaving(true);
    try {
      const html = generateHtml(blocks, subject);
      const payload = { name: name.trim(), subject, html, blocks };
      if (isNew) {
        await templatesAPI.create(payload);
        toast.success('Template criado');
        navigate('/templates');
      } else {
        await templatesAPI.update(id, payload);
        toast.success('Template salvo');
      }
    } catch { toast.error('Erro ao salvar template'); }
    finally { setSaving(false); }
  };

  const selectedBlock = blocks.find(b => b.id === selectedId) || null;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-[#A0462D] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 28px)' }}>

      {/* ── Barra superior ── */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <button onClick={() => navigate('/templates')}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
          <ArrowLeft size={15} /> Voltar
        </button>
        <div className="w-px h-5 bg-gray-200" />
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Nome do template *"
          className="text-sm font-semibold text-gray-900 border-none outline-none w-52 placeholder:text-gray-400 placeholder:font-normal bg-transparent"
        />
        <div className="w-px h-5 bg-gray-200" />
        <input
          value={subject}
          onChange={e => setSubject(e.target.value)}
          placeholder="Assunto do email"
          className="text-sm text-gray-600 border-none outline-none flex-1 placeholder:text-gray-400 bg-transparent"
        />
        <button onClick={() => setPreview(v => !v)}
          className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border transition-colors ${preview ? 'bg-gray-100 border-gray-300 text-gray-700' : 'border-gray-200 text-gray-500 hover:text-gray-800'}`}>
          {preview ? <EyeOff size={14} /> : <Eye size={14} />}
          {preview ? 'Editar' : 'Preview'}
        </button>
        <button onClick={save} disabled={saving}
          className="flex items-center gap-1.5 text-sm text-white px-4 py-1.5 rounded-lg disabled:opacity-50 transition-colors"
          style={{ background: BRAND }}>
          <Save size={14} /> {saving ? 'Salvando…' : 'Salvar'}
        </button>
      </div>

      {/* ── Área principal ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* Painel esquerdo: paleta */}
        <div className="w-44 flex-shrink-0 border-r border-gray-200 bg-gray-50 overflow-y-auto flex flex-col gap-1 p-2">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-2 py-1">Blocos</p>
          {PALETTE.map(({ type, label, Icon, desc }) => (
            <button key={type} onClick={() => addBlock(type)}
              className="flex items-center gap-2 w-full text-left px-2 py-2 rounded-lg text-sm text-gray-700 hover:bg-white hover:shadow-sm transition-all border border-transparent hover:border-gray-200 group">
              <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 bg-gray-100 group-hover:bg-[#FDF6F0] transition-colors">
                <Icon size={14} className="text-gray-500 group-hover:text-[#A0462D]" />
              </div>
              <span className="font-medium text-xs">{label}</span>
            </button>
          ))}
        </div>

        {/* Canvas central */}
        <div className="flex-1 overflow-y-auto bg-gray-100 p-6">
          {preview ? (
            <div className="mx-auto shadow-xl" style={{ maxWidth: 600 }}>
              <iframe
                ref={iframeRef}
                title="preview"
                style={{ width: '100%', minHeight: 500, border: 'none', display: 'block', background: '#fff' }}
              />
            </div>
          ) : (
            <div className="mx-auto bg-white shadow-xl" style={{ maxWidth: 600, minHeight: 400 }}>
              {blocks.length === 0 && (
                <div className="flex flex-col items-center justify-center h-64 text-gray-400 gap-3">
                  <Plus size={32} className="text-gray-200" />
                  <p className="text-sm">Adicione blocos pelo painel esquerdo</p>
                </div>
              )}
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
                  {blocks.map(block => (
                    <SortableBlock
                      key={block.id}
                      block={block}
                      selected={selectedId === block.id}
                      onClick={() => setSelectedId(b => b === block.id ? null : block.id)}
                      onDelete={() => deleteBlock(block.id)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          )}
        </div>

        {/* Painel direito: propriedades */}
        {!preview && (
          <div className="w-64 flex-shrink-0 border-l border-gray-200 bg-white flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Propriedades</p>
            </div>
            <PropertiesPanel
              block={selectedBlock}
              onChange={props => selectedBlock && updateBlockProps(selectedBlock.id, props)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
